import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { ChangeLogMonitorService } from '../../../services/change-log-monitor.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-regeneration-process',
    templateUrl: './regeneration-process.component.html',
    styleUrls: ['./regeneration-process.component.scss', '../../../slt-dashboard.styles.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RegenerationProcessComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  modalTitle = "Regeneneration Process";
  minimise = false;
  isSaveClicked = false;

  // Component-specific properties
  saveloader = false;
  tags: string[] = [];
  leKey: number[] = [];
  dataLevel: string[] = [];
  filingGroupKey: number[] = [];

  // Data properties
  baseURLs: any = {};
  headerData: any = {};
  rowData: any = {};
  filterParams: any = {};
  gridData: any[] = [];
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  globalService: any = {};

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private changeLogMonitorService: ChangeLogMonitorService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.headerData = header;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rowPayload = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rowPayload) ? (rowPayload[0] ?? {}) : (rowPayload ?? {});
    const g = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = Array.isArray(g) ? g : g != null ? [g] : [];
    if (this.gridData.length) {
      this.processGridData();
    }
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    console.log('RegenerationProcess - Component initialized');
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      console.log('RegenerationProcess - init() called with rowData:', this.rowData);
      console.log('RegenerationProcess - gridData:', this.gridData);
      console.log('RegenerationProcess - tags:', this.tags);
      // Initial setup if needed
    } catch (e) {
      console.error('Error in RegenerationProcess init:', e);
    }
  }

  processGridData() {
    console.log('RegenerationProcess - processGridData() called with:', this.gridData);
    // Process grid data to extract tags and keys
    this.tags = [];
    this.leKey = [];
    this.dataLevel = [];
    this.filingGroupKey = [];

    this.gridData.forEach((item: any) => {
      const data = item.data || item;
      console.log('RegenerationProcess - Processing item:', data);
      this.tags.push(data.LEID);
      this.leKey.push(data.LE_KEY);
      this.dataLevel.push(data.DATA_LEVEL);
      this.filingGroupKey.push(data.FILING_GROUP_KEY);
    });

    console.log('RegenerationProcess - Extracted tags:', this.tags);
    this.cdr.detectChanges();
  }

  removeTag(index: number) {
    this.tags.splice(index, 1);
    this.leKey.splice(index, 1);
    this.dataLevel.splice(index, 1);
    this.filingGroupKey.splice(index, 1);
    this.cdr.detectChanges();
  }

  regenerateProcess() {
    this.isSaveClicked = true;
    this.saveloader = true;
    this.cdr.detectChanges();

    const gp = this.globalService?.globalParams || {};

    const params = {
      tax_year: this.filterParams.tax_year || gp?.tax_year,
      le_key: this.leKey.toString(),
      data_level: this.dataLevel.toString(),
      filing_group_key: this.filingGroupKey.toString()
    };

    console.log('RegenerationProcess - Calling API with params:', params);

    this.changeLogMonitorService.saveRegenerateProcess(
      this.GENERAL_CONFIG.bulk_pdf_engine_api_url,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.saveloader = false;

        if (response.callSuccess == "1") {
          this._snackBar.open('Process Regenerated Successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.message || 'Error occurred while regenerating process', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }

        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error regenerating process:', error);
        this.isSaveClicked = false;
        this.saveloader = false;
        this._snackBar.open('Error occurred while regenerating process. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
